// @flow

import * as React from 'react';
import { findDOMNode } from 'react-dom';

import { isFeatureEnabled } from 'common/features';
import FontAwesomeIcon, {
  lightTag,
  lightUserGroup,
  lightLocationDot,
  lightFilterList,
  lightHouseBuilding,
  lightMagnifyingGlass,
  solidCalendarDays
} from 'common/components/FontAwesomeIcon';
import CheckboxGroupInput, {
  getSelectedValues as getSelectedCheckboxGroupInputValues
} from './CheckboxGroupInput';
import DebouncedInputField from './DebouncedInputField';
import type { Params } from 'search/types';
import SearchActionCreators from 'search/actions/SearchActionCreators';
import hardMaxInCapacityRange from 'search/utils/capacityRangeMaxValueHelper';
import styles from './SearchFilters.module.scss';

const MIN_ALLOWED_CAPACITY = 1;
const MAX_ALLOWED_CAPACITY = 10000;

export type Props = {|
  params: Params,
  eventTypes: Array<{| id: number, name: string |}>,
  venueTypes: {|
    featured: Array<{|
      id: number,
      name: string
    |}>,
    other: Array<{|
      id: number,
      name: string
    |}>
  |},
  venueServices: {|
    featured: Array<{|
      id: number,
      name: string
    |}>,
    other: Array<{|
      id: number,
      name: string
    |}>
  |}
|};

class SearchFilters extends React.Component<Props> {
  render(): React.Node {
    return (
      <div className={styles.container}>
        <div className={styles.filterContainer}>
          <div className={styles.filterLabelContainer}>
            <label htmlFor="eventTypes">
              <FontAwesomeIcon
                modifier="fw"
                icon={lightTag}
                className={styles.filterLabelIcon}
              />
              {__('search.search_filters.event_types.label')}
            </label>
          </div>
          <div className={styles.filterFieldContainer}>
            <select
              id="eventTypes"
              ref="eventTypeSelect"
              value={this.props.params.et}
              onChange={this._onEventTypeChange}
              className={styles.eventTypeSelect}
            >
              <option value="">
                {__('search.search_filters.event_types.placeholder')}
              </option>
              {this.props.eventTypes.map(eventType => {
                return (
                  <option
                    value={eventType.id}
                    key={`event-type:${eventType.id}`}
                  >
                    {eventType.name}
                  </option>
                );
              })}
            </select>
          </div>
        </div>
        <div className={styles.filterContainer}>
          <div className={styles.filterLabelContainer}>
            <label htmlFor="people">
              <FontAwesomeIcon
                modifier="fw"
                icon={lightUserGroup}
                className={styles.filterLabelIcon}
              />
              {__('search.search_filters.people.label')}
            </label>
          </div>
          <div className={styles.filterFieldContainer}>
            <div className={styles.searchFiltersCapacityOptions}>
              <DebouncedInputField
                className={styles.searchFiltersCapacityField}
                type="number"
                id="people"
                ref="peopleSelect"
                min={MIN_ALLOWED_CAPACITY.toString()}
                max={MAX_ALLOWED_CAPACITY.toString()}
                value={this._peopleValue()}
                onChange={this._onPeopleChange}
                placeholder={__('search.search_filters.people.placeholder')}
              />

              <select
                className={styles.searchFiltersCapacityField}
                data-testid="search-people-filter-select"
                ref="peopleFilterSelect"
                value={this.props.params.people_filter}
                onChange={this._onPeopleFilterChange}
              >
                <option value="">
                  {__('search.search_filters.people_filter.sitting')}
                </option>
                <option value="standing">
                  {__('search.search_filters.people_filter.standing')}
                </option>
              </select>
            </div>
            <PeopleSectionCapacityInfoText people={this.props.params.people} />
          </div>
        </div>
        <div className={styles.filterContainer}>
          <div className={styles.filterLabelContainer}>
            <label htmlFor="location">
              <FontAwesomeIcon
                modifier="fw"
                icon={lightLocationDot}
                className={styles.filterLabelIcon}
              />
              {__('search.search_filters.location.label')}
            </label>
          </div>
          <div className={styles.filterFieldContainer}>
            <DebouncedInputField
              type="text"
              id="location"
              ref="locationInput"
              placeholder={__('search.search_filters.location.placeholder')}
              value={this.props.params.location}
              onChange={this._onLocationChange}
            />
          </div>
        </div>
        <div className={styles.filterContainer}>
          <div className={styles.filterLabelContainer}>
            <label htmlFor="title">
              <FontAwesomeIcon
                modifier="fw"
                icon={lightMagnifyingGlass}
                className={styles.filterLabelIcon}
              />
              {__('search.search_filters.title.label')}
            </label>
          </div>
          <div className={styles.filterFieldContainer}>
            <DebouncedInputField
              type="text"
              id="title"
              ref="titleInput"
              placeholder={__('search.search_filters.title.placeholder')}
              value={this.props.params.title}
              onChange={this._onTitleChange}
            />
          </div>
        </div>
        <div className={styles.filterContainer}>
          <div className={styles.filterLabelContainer}>
            <label>
              <FontAwesomeIcon
                modifier="fw"
                icon={lightFilterList}
                className={styles.filterLabelIcon}
              />
              {__('search.search_filters.venue_services.label')}
            </label>
            {this.props.params.vs && this.props.params.vs.length > 0 ? (
              <ClearVenueServicesButton />
            ) : null}
          </div>
          <div
            className={styles.checkboxFilterFieldContainer}
            data-testid="venue-service-criteria-area"
            ref="venueServicesCheckboxGroup"
          >
            <CheckboxGroupInput
              featuredValues={this.props.venueServices.featured}
              otherValues={this.props.venueServices.other}
              // $FlowFixMe[incompatible-type] -- Suppressed during Flow upgrade to 0.241.0. Delete this comment to view the error.
              selectedValues={this.props.params.vs}
              onChange={this._onVenueServicesChange}
            />
          </div>
        </div>
        {isFeatureEnabled('venue_type_search') ? (
          <div
            className={styles.filterContainer}
            data-testid="venue-type-criteria-area"
          >
            <div className={styles.filterLabelContainer}>
              <label>
                <FontAwesomeIcon
                  modifier="fw"
                  icon={lightHouseBuilding}
                  className={styles.filterLabelIcon}
                />
                {__('search.search_filters.venue_types.label')}
              </label>
              {this.props.params.vt && this.props.params.vt.length > 0 ? (
                <ClearVenueTypesButton />
              ) : null}
            </div>
            <div
              className={styles.checkboxFilterFieldContainer}
              ref="venueTypesCheckboxGroup"
            >
              <CheckboxGroupInput
                featuredValues={this.props.venueTypes.featured}
                otherValues={this.props.venueTypes.other}
                // $FlowFixMe[incompatible-type] -- Suppressed during Flow upgrade to 0.241.0. Delete this comment to view the error.
                selectedValues={this.props.params.vt}
                onChange={this._onVenueTypeChange}
              />
            </div>
          </div>
        ) : null}

        {isFeatureEnabled('availability_search') ? (
          <div className={styles.filterContainer}>
            <div className={styles.filterLabelContainer}>
              <label htmlFor="availability">
                <FontAwesomeIcon
                  modifier="fw"
                  icon={solidCalendarDays}
                  className={styles.filterLabelIcon}
                />
                Event date
              </label>
            </div>
            <div className={styles.filterFieldContainer}>
              <DebouncedInputField
                type="datetime-local"
                id="availability"
                ref="availabilityInput"
                value={this.props.params.availability}
                onChange={this._onAvailabilityChange}
              />
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  _onTitleChange: () => void = () => {
    const input = findDOMNode(this.refs.titleInput);
    if (!(input instanceof HTMLInputElement)) return;
    const title = input.value;
    SearchActionCreators.changeVenueTitleFilter(title);
  };

  _onVenueServicesChange: () => void = () => {
    const mountedNode = this.refs.venueServicesCheckboxGroup;
    if (!(mountedNode instanceof HTMLElement)) return;

    const venueServices = getSelectedCheckboxGroupInputValues(mountedNode);
    SearchActionCreators.changeVenueServicesFilter(venueServices);
  };

  _onEventTypeChange: () => void = () => {
    const select = findDOMNode(this.refs.eventTypeSelect);
    if (!(select instanceof HTMLSelectElement)) return;
    const selectedEventType = select.value;
    SearchActionCreators.changeEventTypeFilter(selectedEventType);
  };

  _onVenueTypeChange: () => void = () => {
    const mountedNode = this.refs.venueTypesCheckboxGroup;
    if (!(mountedNode instanceof HTMLElement)) return;

    const venueTypes = getSelectedCheckboxGroupInputValues(mountedNode);
    SearchActionCreators.changeVenueTypeFilter(venueTypes);
  };

  _onPeopleChange: () => void = () => {
    const input = findDOMNode(this.refs.peopleSelect);
    if (!(input instanceof HTMLInputElement)) return;
    const peopleAmount = input.value;
    if (peopleAmountValid(peopleAmount)) {
      SearchActionCreators.changePeopleFilter(
        this._capacityRange(peopleAmount)
      );
    }
  };

  _capacityRange: (number | string) => string | number = people => {
    if (people) {
      const maxValue = hardMaxInCapacityRange(people);
      return `${people}..${maxValue}`;
    } else {
      return people;
    }
  };

  _peopleValue: () => string | number = () => {
    const { people = '' } = this.props.params;
    const hardMinForCapacity = firstNumberFromCapacityRange(people);
    if (hardMinForCapacity) {
      return hardMinForCapacity;
    }
    return '';
  };

  _onPeopleFilterChange: () => void = () => {
    const select = findDOMNode(this.refs.peopleFilterSelect);
    if (!(select instanceof HTMLSelectElement)) return;
    const peopleFilter = select.value;
    // TODO: Come up with a better name #peoplefilter
    SearchActionCreators.changePeopleFilterFilter(peopleFilter);
  };

  _onLocationChange: () => void = () => {
    const input = findDOMNode(this.refs.locationInput);
    if (!(input instanceof HTMLInputElement)) return;
    const location = input.value;
    SearchActionCreators.changeLocationFilter(location);
  };

  _onAvailabilityChange: () => void = () => {
    const input = findDOMNode(this.refs.availabilityInput);
    if (!(input instanceof HTMLInputElement)) return;
    const availability = input.value;
    SearchActionCreators.changeAvailabilityFilter(availability);
  };
}

function peopleAmountValid(peopleAmount: string) {
  return (
    // $FlowFixMe[invalid-compare] Fix people type allowing both string and number
    (peopleAmount < MAX_ALLOWED_CAPACITY &&
      // $FlowFixMe[invalid-compare] Fix people type allowing both string and number
      peopleAmount >= MIN_ALLOWED_CAPACITY) ||
    peopleAmount === ''
  );
}

function firstNumberFromCapacityRange(people: string): number | string {
  if (people) {
    const firstNumbersRegex = /^\d+/;
    const firstNumberFromRegex = people.match(firstNumbersRegex);
    if (firstNumberFromRegex) {
      return parseInt(firstNumberFromRegex[0], 10);
    }
  }
  return '';
}

const capacityRangeValues = ({ people = '' }: {| people: void | string |}) => {
  if (people) {
    const hardMinForCapacity = firstNumberFromCapacityRange(people);
    if (
      // $FlowFixMe[invalid-compare] Fix people type allowing both string and number
      hardMinForCapacity >= MIN_ALLOWED_CAPACITY &&
      // $FlowFixMe[invalid-compare] Fix people type allowing both string and number
      hardMinForCapacity <= MAX_ALLOWED_CAPACITY
    ) {
      return {
        min: parseInt(hardMinForCapacity, 10).toString(),
        max: hardMaxInCapacityRange(hardMinForCapacity).toString()
      };
    }
  }
  return {
    min: MIN_ALLOWED_CAPACITY.toString(),
    max: `${MAX_ALLOWED_CAPACITY}+`
  };
};

function PeopleSectionCapacityInfoText(people: {| people: void | string |}) {
  const capacityRangeObject = capacityRangeValues(people);
  return (
    <div className={styles.infoTextContainer}>
      <p
        data-testid="capacity-info-text"
        className={styles.infoText}
        dangerouslySetInnerHTML={{
          __html: I18n.t('search.search_filters.people.info', {
            fromNumber: capacityRangeObject['min'],
            toNumber: capacityRangeObject['max']
          })
        }}
      />
    </div>
  );
}

function ClearVenueTypesButton() {
  return (
    <button
      type="button"
      className={styles.clearSelectionButton}
      onClick={() => {
        SearchActionCreators.changeVenueTypeFilter([]);
      }}
    >
      {__('search.search_filters.clear_selection')}
    </button>
  );
}

function ClearVenueServicesButton() {
  return (
    <button
      type="button"
      className={styles.clearSelectionButton}
      onClick={() => {
        SearchActionCreators.changeVenueServicesFilter([]);
      }}
    >
      {__('search.search_filters.clear_selection')}
    </button>
  );
}

export default SearchFilters;
